import { Card, CardContent } from "@/components/ui/card";
import { Home, ArrowLeft, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

// ترجمه‌ها داخل همین فایل
const translations = {
    fa: {
        metaTitle: "۴۰۴ | صفحه یافت نشد - دلبند",
        logoAlt: "دلبند",
        number404: "404",
        title: "متاسفانه صفحه مورد نظر یافت نشد...",
        description: "درحال حاضر صفحه‌ای که به‌دنبال آن هستید وجود ندارد، ممکن است آدرس اشتباه وارد شده باشد یا صفحه مورد نظر دیگر در دسترس نباشد.",
        goHomeButton: "برو به صفحه اصلی",
        refreshButton: "تلاش مجدد",
    },
    en: {
        metaTitle: "404 | Page Not Found - Delband",
        logoAlt: "Delband",
        number404: "404",
        title: "Unfortunately, the page was not found...",
        description: "The page you are looking for does not currently exist. The address may have been entered incorrectly or the page may no longer be available.",
        goHomeButton: "Go to Homepage",
        refreshButton: "Try Again",
    },
    ar: {
        metaTitle: "404 | الصفحة غير موجودة - دلبند",
        logoAlt: "دلبند",
        number404: "404",
        title: "للأسف، الصفحة المطلوبة غير موجودة...",
        description: "الصفحة التي تبحث عنها غير موجودة حاليًا. ربما تم إدخال العنوان بشكل غير صحيح أو لم تعد الصفحة متاحة.",
        goHomeButton: "الذهاب إلى الصفحة الرئيسية",
        refreshButton: "حاول مرة أخرى",
    },
    tr: {
        metaTitle: "404 | Sayfa Bulunamadı - Delband",
        logoAlt: "Delband",
        number404: "404",
        title: "Maalesef, sayfa bulunamadı...",
        description: "Aradığınız sayfa şu anda mevcut değil. Adres yanlış girilmiş olabilir veya sayfa artık kullanılamayabilir.",
        goHomeButton: "Ana Sayfaya Git",
        refreshButton: "Tekrar Dene",
    },
};

export default function NotFound() {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const currentLang = i18n.language as keyof typeof translations;
    const t = translations[currentLang] || translations.fa;
    const isRTL = ["fa", "ar"].includes(currentLang);

    const [isLoaded, setIsLoaded] = useState(false);
    const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);

    useEffect(() => {
        setIsLoaded(true);
        // Generate floating particles for background animation
        const newParticles = Array.from({ length: 20 }, (_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            delay: Math.random() * 2,
        }));
        setParticles(newParticles);
    }, []);

    const handleGoHome = () => {
        navigate("/");
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <div className="min-h-screen w-full relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-orange-50" dir={isRTL ? "rtl" : "ltr"}>
            <Helmet>
                <title>{t.metaTitle}</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* Animated Background Particles */}
            <div className="absolute inset-0 overflow-hidden">
                {particles.map((particle) => (
                    <div
                        key={particle.id}
                        className="absolute w-2 h-2 bg-blue-200/30 rounded-full animate-float"
                        style={{
                            left: `${particle.x}%`,
                            top: `${particle.y}%`,
                            animationDelay: `${particle.delay}s`,
                            animationDuration: `${3 + Math.random() * 2}s`,
                        }}
                    />
                ))}
            </div>

            {/* Main Content */}
            <div className="min-h-screen w-full flex items-center justify-center p-4 relative z-10">
                <Card
                    className={`w-full max-w-2xl mx-4 backdrop-blur-xl bg-white/20 border-white/30 shadow-2xl transition-all duration-1000 transform ${isLoaded ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                        }`}
                >
                    <CardContent className="p-8 md:p-12 text-center">
                        {/* Logo with Animation */}
                        <div className="mb-8 flex justify-center">
                            <div className="relative group cursor-pointer transition-transform duration-300 hover:scale-110">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-orange-400 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                                <img src="../../media/logo-brand.svg" alt={t.logoAlt} className="relative w-32 h-auto md:w-40 drop-shadow-lg animate-pulse-slow" />
                            </div>
                        </div>

                        {/* 404 Number with Glassmorphism */}
                        <div className="mb-6">
                            <h1 className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent animate-bounce-slow">{t.number404}</h1>
                        </div>

                        {/* Main Title */}
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 animate-fade-in-up">{t.title}</h2>

                        {/* Description */}
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 max-w-lg mx-auto animate-fade-in-up animation-delay-200">{t.description}</p>

                        {/* Action Buttons */}
                        <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animation-delay-400 ${isRTL ? "sm:flex-row-reverse" : ""}`}>
                            <button
                                onClick={handleGoHome}
                                className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-3 min-w-[200px]"
                            >
                                <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <Home className={`w-5 h-5 relative z-10 ${isRTL ? "order-2" : "order-1"}`} />
                                <span className={`relative z-10 ${isRTL ? "order-1" : "order-2"}`}>{t.goHomeButton}</span>
                            </button>

                            <button
                                onClick={handleRefresh}
                                className="group relative px-8 py-4 bg-white/30 backdrop-blur-sm border border-white/50 text-gray-700 font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:bg-white/40 transform hover:scale-105 transition-all duration-300 flex items-center gap-3 min-w-[200px]"
                            >
                                <RefreshCw className={`w-5 h-5 group-hover:rotate-180 transition-transform duration-500 ${isRTL ? "order-2" : "order-1"}`} />
                                <span className={isRTL ? "order-1" : "order-2"}>{t.refreshButton}</span>
                            </button>
                        </div>

                        {/* Decorative Elements */}
                        <div className={`mt-12 flex justify-center ${isRTL ? "space-x-reverse" : ""} space-x-2`}>
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="w-3 h-3 bg-gradient-to-r from-blue-400 to-orange-400 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }}></div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Custom Styles */}
            <style>{`
                @keyframes float {
                    0%,
                    100% {
                        transform: translateY(0px) rotate(0deg);
                    }
                    50% {
                        transform: translateY(-20px) rotate(180deg);
                    }
                }

                @keyframes bounce-slow {
                    0%,
                    100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }

                @keyframes pulse-slow {
                    0%,
                    100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.7;
                    }
                }

                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }

                .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                }

                .animate-pulse-slow {
                    animation: pulse-slow 3s ease-in-out infinite;
                }

                .animate-fade-in-up {
                    animation: fade-in-up 0.8s ease-out forwards;
                }

                .animation-delay-200 {
                    animation-delay: 0.2s;
                }

                .animation-delay-400 {
                    animation-delay: 0.4s;
                }
            `}</style>
        </div>
    );
}
